import { convertToTaskParameters } from './helpers';
import type { WorkflowConfigV0 } from './migrationV0';
import type {
  LabwarePropertiesV1,
  LabwareTypeV1,
  LoadedWorkflowV1,
  TaskPropertiesV1,
} from './migrationV1';
import type { MigrationFn } from '../runMigrationChain';

// In v0, we had to save this magic string because we couldn't save null.
// https://github.com/automata-tech/prototype-frontend-editor/pull/244#issuecomment-2251444388
const NULL_LABWARE_TYPE = 'null_labware_type';

export * from './migrationV1';

// This is _almost_ a MigrationFn<LoadedWorkflowV0, LoadedWorkflowV1>, but it needs WorkflowConfig
// because v0 didn't store everything Visual Builder needs into `layout.data`.
export const _migrateTo1 = (
  workflowConfig: WorkflowConfigV0
): LoadedWorkflowV1 => {
  const layoutData = workflowConfig.workflow.layout.data;

  // TODO(IL, 2024-09-04): could do zod check at head of migration
  if (typeof layoutData !== 'string') {
    throw new Error(
      `Invalid workflow pre-v1 layout data. Maybe this workflow was saved without layout info (layoutData: ${layoutData})`
    );
  }

  // NOTE: currently, the graph data is a stringified json... inside a json...
  // Also, in this version, we only had the graph slice.
  const deserialisedGraphSlice: LoadedWorkflowV1['graph'] =
    JSON.parse(layoutData);

  // This key is relatively new, so we've been migrating it upon load
  deserialisedGraphSlice.taskDependencies =
    deserialisedGraphSlice.taskDependencies ?? {};

  const labwareProperties = workflowConfig.workflow.labware.reduce<
    LoadedWorkflowV1['visualWorkflow']['labwareProperties']
  >((acc, item) => {
    const getLabwareTypeOrDefault = (type: string): LabwareTypeV1 => {
      const lowercaseType = type.toLowerCase();
      const labwareTypes: LabwareTypeV1[] = [
        'tiprack',
        'reservoir',
        'tuberack',
        'microplate',
      ];
      return labwareTypes.includes(lowercaseType as LabwareTypeV1)
        ? (lowercaseType as LabwareTypeV1)
        : 'microplate';
    };

    const labware: LabwarePropertiesV1 = {
      id: item.id,
      startLocation: item.location.instrument || null,
      labwareType: getLabwareTypeOrDefault(item.description ?? ''),
      closureState: 'open',
      content: 'empty',
      contentDescription: '',
      labwareDefinitionURI: item.type === NULL_LABWARE_TYPE ? null : item.type,
      nickname: item.description ?? '',
      singleOrMultiUse: 'single',
      maxIdleTimeExists: false,
      maxIdleTime: null,
    };

    return {
      ...acc,
      [item.id]: labware,
    };
  }, {});

  const taskProperties = Object.entries(workflowConfig.workflow.tasks).reduce(
    (acc, [taskId, task]) => {
      const properties: TaskPropertiesV1 = {
        id: taskId,
        taskType: task.action,
        instrumentId: task.instrument.type,
        nickname: task.action,
        taskParameters:
          task.arguments == null || !('static' in task?.arguments)
            ? null
            : convertToTaskParameters(task.arguments),
        userTimeEstimate: task.time_estimate,
      };

      return {
        ...acc,
        [taskId]: properties,
      };
    },
    {}
  );

  const visualWorkflow = {
    parameterDefinitions: workflowConfig.workflow.parameterDefinitions,
    labwareProperties,
    taskProperties,
    workflowAuthor: workflowConfig.metadata.author,
    workflowDescription: workflowConfig.metadata.description,
    workflowName: workflowConfig.name,
    comments: {} /* No comments were saved in v0 :( */,
    workcellName:
      'Experiment 1 Workcell' /* workcell name was not saved in v0 */,
  } satisfies LoadedWorkflowV1['visualWorkflow'];

  return {
    graph: deserialisedGraphSlice,
    visualWorkflow,
  };
};

/** A pass-through placeholder for v0 -> v1 migration, to preserve meaning of indicies in this array.
 * The actual `_migrateTo1` fn isn't in the chain because it's an exception to the pattern,
 * it's handled as a special case in the migrateLoadedVisualBuilderData fn.
 */
export const _dummyMigrateTo1: MigrationFn<any, LoadedWorkflowV1> = (
  layoutData
) => layoutData;
